import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, computed, input, InputSignal, Signal, TemplateRef, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { routerSegments } from '@consts/routes';
import { PermissionModel } from '@models/permission.model';
import { select, Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { EdgeSelectors } from '@states/edge/edge.selector-types';
import { PermissionSelectors } from '@states/permissions/permissions.selector-types';
import { Dictionary } from 'lodash';
import { switchMap, take } from 'rxjs';
import { AuthorizationModule } from 'src/app/authorization/authorization.module';
import {
  EdgeCamera,
  TestCamerasFlatEdgeRow,
  TestCamerasFlatRow,
  TestCamerasRowType,
} from 'src/app/cameras/camera.model';
import { Edge } from 'src/app/edge/edge.model';
import { TruncateDirective } from 'src/app/shared/directives/truncate.directive';
import { UiCameraSnapshotComponent } from 'src/app/shared/ui-kit/ui-camera-snapshot/ui-camera-snapshot.component';
import {
  CardTemplateContext,
  CustomRowContext,
  DatasetTableConfig,
  UiDatasetTableComponent,
} from '@ui/lib/ui-kit/ui-dataset-table';
import {
  CoreInfoModalComponent,
  CoreInfoModalData,
} from '../test-camera-table/components/core-info-modal/core-info-modal.component';
import {
  createTableConfig,
  CreateTableConfigDependencies,
  TestCameraTableCellContext,
} from './test-camera-one-level-array-table.util';

@Component({
  selector: 'test-camera-one-level-array-table',
  imports: [
    CommonModule,
    MatIcon,
    TranslatePipe,
    TruncateDirective,
    DecimalPipe,
    UiCameraSnapshotComponent,
    AuthorizationModule,
    UiDatasetTableComponent,
  ],
  templateUrl: './test-camera-one-level-array-table.component.html',
  styleUrl: './test-camera-one-level-array-table.component.scss',
})
export class TestCameraOneLevelArrayTableComponent {
  public testCamerasRowsSig: InputSignal<TestCamerasFlatRow[]> = input<TestCamerasFlatRow[]>();
  public TestCamerasRowType = TestCamerasRowType;

  public tableDataSig = computed<TestCamerasFlatRow[]>(() => this.testCamerasRowsSig() ?? []);

  public selectAllCamerasSig: Signal<Dictionary<EdgeCamera.CameraItem>> =
    this.store$.selectSignal(CameraSelectors.selectAllCamerasEntities);
  public selectAllEdgesSig: Signal<Dictionary<Edge.EdgeDocument>> =
    this.store$.selectSignal(EdgeSelectors.selectEdgesLookup);

  protected readonly edgeHeaderTemplate = viewChild<TemplateRef<CustomRowContext<TestCamerasFlatRow>>>(
    'edgeHeader',
  );
  protected readonly edgeCardTemplate =
    viewChild<TemplateRef<CardTemplateContext<TestCamerasFlatRow>>>('edgeCardTemplate');
  protected readonly cameraCellTemplate =
    viewChild<TemplateRef<TestCameraTableCellContext>>('cameraCell');
  protected readonly mainBitrateCellTemplate =
    viewChild<TemplateRef<TestCameraTableCellContext>>('mainBitrateCell');
  protected readonly mainDropCellTemplate =
    viewChild<TemplateRef<TestCameraTableCellContext>>('mainDropCell');
  protected readonly subBitrateCellTemplate =
    viewChild<TemplateRef<TestCameraTableCellContext>>('subBitrateCell');
  protected readonly subDropCellTemplate =
    viewChild<TemplateRef<TestCameraTableCellContext>>('subDropCell');

  private readonly cameraEditPermissions = computed(() => {
    const rows = this.testCamerasRowsSig() ?? [];
    const cameraIds = rows
      .filter((r) => r.type === TestCamerasRowType.Camera)
      .map((r) => r.cameraId);
    if (cameraIds.length === 0) return {};
    return this.store$.selectSignal(CameraSelectors.selectCameraEditPermissionsMap(cameraIds))();
  });

  public tableConfig = computed<DatasetTableConfig<TestCamerasFlatRow>>(() => {
    const edgeTemplate = this.edgeHeaderTemplate();
    const edgeCardTpl = this.edgeCardTemplate();
    const cameraTemplate = this.cameraCellTemplate();
    const mainBitrateTemplate = this.mainBitrateCellTemplate();
    const mainDropTemplate = this.mainDropCellTemplate();
    const subBitrateTemplate = this.subBitrateCellTemplate();
    const subDropTemplate = this.subDropCellTemplate();

    if (
      !edgeTemplate ||
      !cameraTemplate ||
      !mainBitrateTemplate ||
      !mainDropTemplate ||
      !subBitrateTemplate ||
      !subDropTemplate
    ) {
      return { columns: [] };
    }

    const permissions = this.cameraEditPermissions();
    const deps: CreateTableConfigDependencies = {
      edgeHeaderTemplate: edgeTemplate,
      cameraCellTemplate: cameraTemplate,
      mainBitrateCellTemplate: mainBitrateTemplate,
      mainDropCellTemplate: mainDropTemplate,
      subBitrateCellTemplate: subBitrateTemplate,
      subDropCellTemplate: subDropTemplate,
      canEditCamera: (cameraId: string) => permissions[cameraId] === true,
      customCardRowTemplate:
        edgeCardTpl ?
          (row) => (row.type === TestCamerasRowType.Edge ? edgeCardTpl : null)
        : undefined,
    };

    return createTableConfig(deps);
  });

  constructor(
    private store$: Store,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  public openCoreInfoModal(edgeRow: TestCamerasFlatEdgeRow): void {
    const edge = this.selectAllEdgesSig()[edgeRow.edgeId];
    if (!edge) return;

    const modalData: CoreInfoModalData = {
      edgeName: edge.name,
      eth0Connected: !!edgeRow.eth0connected,
      eth0Speed: edgeRow.eth0speed,
      eth1Connected: !!edgeRow.eth1connected,
      eth1Speed: edgeRow.eth1speed,
      totalLoad: edgeRow.totalLoad,
    };

    this.dialog.open(CoreInfoModalComponent, {
      data: modalData,
      width: '400px',
      autoFocus: false,
    });
  }

  public onRowAction(event: { actionId: string; row: TestCamerasFlatRow }): void {
    if (event.actionId === 'edit' && event.row.type === TestCamerasRowType.Camera) {
      this.gotoCameraEdit(event.row.cameraId);
    }
  }

  public isEditCameraPermission(cameraId: string) {
    return this.store$.pipe(
      select(CameraSelectors.selectCameraById(cameraId)),
      switchMap((camera) => {
        return this.store$.pipe(
          select(
            PermissionSelectors.checkAccessPermissionsOR(
              [PermissionModel.Permissions.CameraEdit],
              [cameraId, camera.edgeId, camera.locationId],
            ),
          ),
        );
      }),
      take(1),
    );
  }

  public gotoCameraEdit(cameraId: string) {
    this.store$
      .select(CameraSelectors.selectCameraById(cameraId))
      .pipe(take(1))
      .subscribe((camera) => {
        if (camera) {
          this.router.navigateByUrl(
            `/${routerSegments.cameraEdit}/${routerSegments.edit}/${camera.locationId}/${camera.edgeId}/${cameraId}/details`,
          );
        }
      });
  }
}
